import RiderNav from "../../components/RiderNav";
import Collapsible from "../../components/Collapsible";

export default function RiderGuide() {
  return (
    <div className="rider-app">
      <RiderNav />
      <div className="page wide page-with-nav">
        <div className="brand-header">
          <span className="brand-logo">📖</span>
          <span className="brand-name">Dispatcher Guide</span>
          <p className="brand-tagline">How to get approved, go online, and get paid</p>
        </div>

        <Collapsible title="✅ Getting Approved" defaultOpen className="delivery-card">
          <p className="hint" style={{ marginBottom: "8px" }}>
            After registering, your account status is <strong>pending</strong> until an admin reviews
            your KYC — you'll see an "Application Under Review" screen until then, and can't go online
            yet. Once approved, the Dashboard unlocks fully.
          </p>
          <p className="hint">
            Bicycle dispatchers don't need to register a vehicle plate — that field is skipped for you
            during registration and on your Profile.
          </p>
          <p className="hint" style={{ marginTop: "8px" }}>
            Your Profile page shows a completeness percentage — keep it above 70% (all KYC fields,
            guarantor details, and verification documents filled in) or a banner will keep reminding
            you until it's done.
          </p>
        </Collapsible>

        <Collapsible title="⚡ Going Online & Getting Jobs" className="delivery-card">
          <p className="hint" style={{ marginBottom: "8px" }}>
            Toggle online from your Dashboard. When a nearby order matches your vehicle type, you'll
            get an offer with <strong>30 seconds</strong> to Accept or Pass — if you don't respond in
            time, it automatically moves to the next nearest dispatcher.
          </p>
          <p className="hint" style={{ marginBottom: "8px" }}>
            If nobody's nearby (or everyone passed), the order lands on the <strong>Job Board</strong>{" "}
            instead, where any eligible dispatcher can claim it first-come-first-served.
          </p>
          <ul style={{ paddingLeft: "18px", fontSize: "14px", color: "var(--text-secondary)", lineHeight: 1.7 }}>
            <li>You only see jobs matching your own vehicle type (bike/van/truck) — customers don't choose "Bicycle" directly, it's not a separate job category.</li>
            <li><strong>🚲 Bicycle</strong> dispatchers are pooled into eligible <strong>Bike</strong> jobs automatically — you'll only be offered/can only claim ones under 10km and under 10kg, at the same fare a bike job would pay.</li>
            <li><strong>🚚 Truck</strong> jobs only appear once an admin (or the 10-minute auto-quote fallback) has actually priced them.</li>
            <li>You can hold up to a few active jobs at once, along the same route.</li>
          </ul>
        </Collapsible>

        <Collapsible title="🚗 Completing a Delivery" className="delivery-card">
          <p className="hint" style={{ marginBottom: "8px" }}>
            Move the order through each stage from your active job card: <em>Accepted → Arrived at
            Pickup → Picked Up → In Transit → Delivered</em>. Multi-stop orders show each stop
            separately — complete them one at a time, in order.
          </p>
          <p className="hint">
            Before you can mark an order <strong>Delivered</strong>, you need the receiver's
            verification code (or, for paid Receiver Acknowledgement orders, their signature/photo/ID
            photo instead) — this confirms the right person actually received the package. If a
            receiver genuinely can't produce it, you can request a support override.
          </p>
        </Collapsible>

        <Collapsible title="💵 Payment — What to Collect" className="delivery-card">
          <p className="hint" style={{ marginBottom: "8px" }}>Each job clearly shows what (if anything) to collect:</p>
          <ul style={{ paddingLeft: "18px", fontSize: "14px", color: "var(--text-secondary)", lineHeight: 1.7 }}>
            <li><strong>📋 Paid by B2B/Corporate</strong> — already billed to the business account; don't collect anything from the receiver.</li>
            <li><strong>💵 Cash on Delivery</strong> — collect the amount shown from the receiver.</li>
            <li><strong>✅ Paid</strong> — already settled by card or wallet; don't collect.</li>
            <li><strong>⚠️ Not Paid</strong> — genuinely still unpaid; use judgment before handing over the package.</li>
          </ul>
        </Collapsible>

        <Collapsible title="💰 Wallet & Payouts" className="delivery-card">
          <p className="hint">
            Your earnings and any cash-collected commission owed to the platform are tracked in your
            Wallet tab. Set up your payout account (bank details) there, and choose manual or daily
            automatic sweep. Trip History shows every completed delivery.
          </p>
        </Collapsible>

        <Collapsible title="🚨 Raising an Issue" className="delivery-card">
          <p className="hint">
            If something goes wrong on a job (wrong address, unreachable receiver, etc.), use "Raise
            Issue" on the active order — this notifies admin and gets logged against that order.
          </p>
        </Collapsible>
      </div>
    </div>
  );
}
